import { IconChevronRight, IconClock, IconUser } from '@tabler/icons-react-native';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { localeAwareFormat, localeAwareFormatDistance } from 'app/lib/date';
import { useTheme } from 'app/shopify';
import type { ListingSubtype, ListingType } from 'app/types/listing';
import { isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';

export interface StoryLoanContext {
  start?: string;
  end?: string;
  returned?: string;
  lender?: { profile?: { firstName?: string; picture?: string | null } | null };
  listing: {
    item: { name: string };
    picture?: string | null;
    subtype?: ListingSubtype;
    type: ListingType;
  };
}

export function StoryLoanCard({
  loan,
  onPress,
  testID,
}: {
  loan: StoryLoanContext;
  onPress?: () => void;
  testID?: string;
}) {
  const theme = useTheme();
  const { t } = useTranslation('posts');
  const name = loan.lender?.profile?.firstName || t('storyComposer.neighbour');
  const endedAt = new Date(loan.returned ?? loan.end ?? '');
  const startedAt = new Date(loan.start ?? '');
  const timeAgo = isValid(endedAt)
    ? t('storyComposer.timeAgo', { time: localeAwareFormatDistance(endedAt, new Date()) })
    : null;
  const date = isValid(startedAt) ? localeAwareFormat(startedAt, 'PP') : null;

  const content = (
    <Row
      alignItems="center"
      gap="spacing.general.x3"
      backgroundColor="surface.primary-neutral"
      borderRadius="radius.lg"
      padding="spacing.general.x3"
      width="100%"
    >
      <Column borderRadius="radius.md" overflow="hidden">
        <ListingThumbnail
          picture={loan.listing.picture}
          size={onPress ? 76 : 60}
          subtype={loan.listing.subtype}
          type={loan.listing.type}
          hideCorpoBadge
        />
      </Column>
      <Column flex={1} gap="spacing.general.x1" minWidth={0}>
        <Text fontSize={15} lineHeight={20} fontWeight="700" numberOfLines={2}>
          {loan.listing.item.name}
        </Text>
        <Row alignItems="center" gap="spacing.general.x1">
          {loan.lender?.profile?.picture ? (
            <PCImage
              source={{ uri: loan.lender.profile.picture }}
              width={20}
              height={20}
              style={{ borderRadius: 10 }}
              accessibilityElementsHidden
            />
          ) : (
            <IconUser size={16} color={theme.colors['icon.secondary']} />
          )}
          <Text color="text.secondary" fontSize={12} lineHeight={18} flexShrink={1} numberOfLines={2}>
            {t('storyComposer.borrowedFrom', { name })}
          </Text>
        </Row>
        {timeAgo ? (
          <Row alignItems="center" gap="spacing.general.x1">
            <IconClock size={14} color={theme.colors['icon.secondary']} />
            <Text color="text.secondary" fontSize={12} lineHeight={18} flexShrink={1}>
              {timeAgo}
            </Text>
          </Row>
        ) : null}
        {onPress && date ? (
          <Text color="text.secondary" fontSize={11} lineHeight={16}>
            {t('storyComposer.borrowedOn', { date })}
          </Text>
        ) : null}
      </Column>
      {onPress ? <IconChevronRight size={18} color={theme.colors['icon.secondary']} /> : null}
    </Row>
  );

  return onPress ? (
    <Button
      variant="plain"
      accessibilityRole="button"
      pressableType={PressableType.GestureHandler}
      onPress={onPress}
      testID={testID}
      accessibilityLabel={[loan.listing.item.name, t('storyComposer.borrowedFrom', { name }), timeAgo, date]
        .filter(Boolean)
        .join('. ')}
    >
      {content}
    </Button>
  ) : (
    content
  );
}
